// Snapping engine for canvas drags and placements. Targets per axis, in
// priority order: the canvas center, the boundary's tangent lines (its bbox
// edges, which for a circle are the true tangents), then halves, thirds, and
// quarters of the canvas. Anything else falls back to a 1 mm grid, so drags
// land on round millimetres. Non-grid snaps report guides for drawing.

import type { DocumentModel, Vec2 } from '../core/types'
import { boundaryBBox } from '../core/boundary'

export type SnapKind = 'guide' | 'center' | 'tangent' | 'fraction' | 'grid'

export interface SnapGuide {
  axis: 'x' | 'y'
  value: number
  kind: SnapKind
}

interface Target {
  v: number
  kind: SnapKind
  prio: number
}

const FRACTIONS = [1 / 4, 1 / 3, 1 / 2, 2 / 3, 3 / 4]

function axisTargets(size: number, bmin: number, bmax: number): Target[] {
  const out: Target[] = [{ v: size / 2, kind: 'center', prio: 0 }]
  out.push({ v: bmin, kind: 'tangent', prio: 1 }, { v: bmax, kind: 'tangent', prio: 1 })
  for (const f of FRACTIONS) {
    if (f !== 1 / 2) out.push({ v: size * f, kind: 'fraction', prio: 2 })
  }
  return out
}

function snapAxis(v: number, targets: Target[], tol: number): { v: number; guide: Target | null } {
  let best: Target | null = null
  let bestDist = Infinity
  for (const t of targets) {
    const d = Math.abs(v - t.v)
    if (d > tol) continue
    if (best === null || t.prio < best.prio || (t.prio === best.prio && d < bestDist)) {
      best = t
      bestDist = d
    }
  }
  if (best) return { v: best.v, guide: best }
  return { v: Math.round(v), guide: null } // 1 mm grid fallback
}

// excludeGuideId keeps a guide being dragged from snapping to itself.
export function snapWorldPoint(
  doc: DocumentModel,
  p: Vec2,
  tol: number,
  excludeGuideId?: string,
): { x: number; y: number; guides: SnapGuide[] } {
  const bb = boundaryBBox(doc)
  const tx = axisTargets(doc.canvas.width, bb.minX, bb.maxX)
  const ty = axisTargets(doc.canvas.height, bb.minY, bb.maxY)
  // User guides outrank every implicit target.
  for (const g of doc.guides) {
    if (g.id === excludeGuideId) continue
    ;(g.axis === 'x' ? tx : ty).push({ v: g.value, kind: 'guide', prio: -1 })
  }
  const rx = snapAxis(p.x, tx, tol)
  const ry = snapAxis(p.y, ty, tol)
  const guides: SnapGuide[] = []
  if (rx.guide) guides.push({ axis: 'x', value: rx.v, kind: rx.guide.kind })
  if (ry.guide) guides.push({ axis: 'y', value: ry.v, kind: ry.guide.kind })
  return { x: rx.v, y: ry.v, guides }
}
